import type { SeatPlayer } from '../shared/components/AvatarFrame.js';

/**
 * View-model types for the Discussion phase and the reveal arc it transitions
 * into (RoleReveal → GhostGuessing → GuessReveal), stopping before GameEnd.
 *
 * The production `stateToDiscussionView` mapper (and the reveal-arc equivalents)
 * are thin projections of these types. Field names deliberately track the server
 * vocabulary (`state.votes` of voterId→targetId incl. VOTE_ANOTHER_ROUND,
 * `pendingEliminationId`, `revealedRole`, `ghostGuessText`) so the mapping stays
 * faithful.
 */

export type Pov = 'villager' | 'fool' | 'ghost';

/** A revealed role, public once `Player.revealedRole` is set server-side. */
export type RevealedRole = 'villager' | 'fool' | 'ghost';

/**
 * Sentinel target id for "call another round" votes. Mirrors the server's
 * `VOTE_ANOTHER_ROUND` ('__another_round__'); duplicated as a client const so
 * the UI doesn't reach into server schema just for the string.
 */
export const ANOTHER_ROUND = '__another_round__';

export type SecretWordTruth =
  | { mode: 'word'; value: string }
  | { mode: 'length'; value: number };

/**
 * A player in the discussion / reveal-arc cast. Carries the clue each gave in
 * clue-giving (public; persists onto the cast cell as the evidence the room
 * votes on and the Ghost later deduces from). `null` clue = never submitted
 * (an AFK player who missed their turn).
 */
export interface DiscussionPlayer extends SeatPlayer {
  turnOrder: number;
  connected: boolean;
  clue: string | null;
}

/** An eliminated player plus their now-public role, for the reveal plate. */
export interface EliminatedReveal {
  player: SeatPlayer;
  role: RevealedRole;
}

/**
 * Discussion resolution beat. `voting` is the live, re-castable state. The
 * other three are the locked outcome the phase resolves to (server tally →
 * RoleReveal); the UI shows the lock as the closing beat of Discussion.
 */
export type DiscussionResolution =
  | { kind: 'voting' }
  | { kind: 'eliminate'; targetId: string }
  | { kind: 'anotherRound' }
  | { kind: 'split' };

/** Discussion (voting) phase view. */
export interface DiscussionView {
  meId: string;
  /**
   * False when the local viewer has been eliminated (dead, still watching).
   * `players` carries only the alive cast, so the viewer's own aliveness is
   * surfaced explicitly rather than derived from "me absent from players".
   * The server already rejects dead votes; the ballot renders as the
   * non-interactive spectating grid when this is false.
   */
  meAlive: boolean;
  pov: Pov;
  /** Alive players, sorted ascending by turnOrder (same order as clue-giving). */
  players: DiscussionPlayer[];
  /**
   * voterId → targetId. targetId is a player id OR `ANOTHER_ROUND`. PUBLIC —
   * every alive client sees the full tally (unlike ghost-meeting's
   * GHOSTS-tagged votes), which is why the cluster carries the real overflow
   * load here.
   */
  votes: ReadonlyMap<string, string>;
  /** `voting` during the live phase; otherwise the locked outcome beat. */
  resolution: DiscussionResolution;
  /** Recto identity — the viewer's own role secret (POV-dependent). */
  secret: SecretWordTruth;
  phaseEndsAt: number | null;
  durationMs: number;
}

/** RoleReveal phase view. The palette is warm-candlelit for every viewer
 *  (the reveal is a public staged beat, not a per-POV surface). */
export interface RoleRevealView {
  meId: string;
  /** The discussion cast as it was at resolution, including the just-struck,
   *  sorted by turnOrder. The component removes struck cells at step 2. */
  cast: DiscussionPlayer[];
  /** The vote-eliminated player (the recto's face-up figure). */
  eliminated: EliminatedReveal;
  /** AFK non-voters eliminated in the same resolution — text-only list. */
  alsoEliminated: EliminatedReveal[];
}

/** GhostGuessing phase view. Palette persists per-viewer (brief §4): the
 *  guessing Ghost (and any Ghost viewer) sees their cool-night world; the
 *  village watches in warm candlelight. */
export interface GhostGuessingView {
  meId: string;
  /** Spread palette for the local viewer: `'ghost'` (cool night) for a Ghost
   *  viewer, `'village'` (warm candle) for Villager/Fool. */
  pov: 'village' | 'ghost';
  /** Post-resolution cast; the struck Ghost stays visibly struck (it is the
   *  subject of the beat). */
  cast: DiscussionPlayer[];
  /** The eliminated Ghost now guessing. */
  guessingGhost: SeatPlayer;
  /** True when the local viewer IS that Ghost (renders the mono input rather
   *  than the room's watching line). */
  isMine: boolean;
  /** Letter count of the secret, for the Ghost's length sentence. */
  secretLength: number;
  phaseEndsAt: number | null;
  durationMs: number;
}

/** GuessReveal phase view. Palette persists per-viewer (see GhostGuessingView).
 *  Shows only what the Ghost guessed — no secret word, no verdict (GameEnd). */
export interface GuessRevealView {
  meId: string;
  pov: 'village' | 'ghost';
  cast: DiscussionPlayer[];
  guessingGhost: SeatPlayer;
  /** The Ghost's submitted guess (`state.ghostGuessText`). */
  guess: string;
}
